import { useState } from 'react';
import { useStartAttempts } from '@/hooks/useStartSuccess';
import { attemptRows } from '@/lib/sessionOutcome';
import pageStyles from './ChargerDetailPage.module.css';
import styles from './StartAttemptsSection.module.css';

/**
 * Session success on the charger page: this charger's start attempts for 30
 * days (app, card and staff), each with its cause and the fix it maps to,
 * newest first, with a "Failed only" switch. A start the wrong-connector
 * redirect sent to the other connector reads "1 to 2". Hidden when the
 * caller cannot read the dashboard (one 403, no retry).
 */
export function StartAttemptsSection({ chargerId, onNavigate }: { chargerId: string; onNavigate: (path: string) => void }) {
  const [failedOnly, setFailedOnly] = useState(false);
  const q = useStartAttempts(chargerId, '30d');
  if (q.isError) return null;
  const rows = q.data ? attemptRows(q.data.attempts, failedOnly) : [];
  return (
    <div className={pageStyles.section}>
      <div className={styles.head}>
        <h2 className={pageStyles.sectionTitle}>Start attempts (30d)</h2>
        <button type="button" className={styles.toggle} aria-pressed={failedOnly} onClick={() => setFailedOnly((v) => !v)}>
          Failed only
        </button>
      </div>
      {q.isPending && <p className={styles.empty}>Loading start attempts...</p>}
      {q.data && rows.length === 0 && (
        <p className={styles.empty}>{failedOnly ? 'No failed starts in 30 days.' : 'No start attempts recorded in 30 days.'}</p>
      )}
      {rows.length > 0 && (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.id} className={`${styles.row} ${r.failed ? styles.failed : ''}`}>
              <span className={styles.time}>{r.at}</span>
              <span>{r.source}</span>
              <span className={styles.connector}>{r.connector ? `#${r.connector}` : ''}</span>
              <span className={styles.result}>{r.result}</span>
              <span className={styles.cause}>
                {r.cause}
                {r.fix && <span className={styles.fix}>Fix: {r.fix}</span>}
              </span>
              {r.sessionId
                ? <button type="button" className={styles.link} onClick={() => onNavigate(`/sessions/${r.sessionId}`)}>Session</button>
                : <span />}
            </li>
          ))}
        </ul>
      )}
      {q.data?.causes_collapsed && <p className={styles.note}>Starts the driver or the vehicle ended read as one.</p>}
    </div>
  );
}
